'use client';

import React from 'react';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/Footer';
import { useTranslation } from '@/hooks/useTranslation';
import Icon from '@/components/ui/AppIcon';

export default function QualityStandardsPage() {
    const { t } = useTranslation();

    const certifications = [
        { name: t('iso9001'), desc: t('iso9001Desc'), icon: 'DocumentCheckIcon' },
        { name: t('iso14001'), desc: t('iso14001Desc'), icon: 'GlobeAltIcon' },
        { name: t('oekoTex'), desc: t('oekoTexDesc'), icon: 'ShieldCheckIcon' },
        { name: t('gots'), desc: t('gotsDesc'), icon: 'SparklesIcon' },
        { name: t('bci'), desc: t('bciDesc'), icon: 'CheckBadgeIcon' },
        { name: t('wrap'), desc: t('wrapDesc'), icon: 'GlobeAltIcon' },
    ];

    const qualityProcess = [
        { title: t('initialVerification'), desc: t('initialVerificationDesc'), icon: 'DocumentTextIcon' },
        { title: t('facilityInspection'), desc: t('facilityInspectionDesc'), icon: 'BuildingOffice2Icon' },
        { title: t('sampleTesting'), desc: t('sampleTestingDesc'), icon: 'BeakerIcon' },
        { title: t('ongoingMonitoring'), desc: t('ongoingMonitoringDesc'), icon: 'ChartBarIcon' },
    ];

    return (
        <>
            <Header />
            <main className="min-h-screen bg-background">
                {/* Hero */}
                <section className="py-20 px-6 bg-gradient-to-b from-card to-background border-b border-border">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="inline-flex items-center space-x-2 px-4 py-2 bg-verified/10 rounded-full text-verified text-sm font-semibold mb-6">
                            <Icon name="ShieldCheckIcon" size={16} variant="solid" />
                            <span>{t('certifiedStandards')}</span>
                        </div>
                        <h1 className="text-5xl font-headline font-bold text-foreground mb-6">
                            {t('qualityStandardsTitle')}
                        </h1>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            {t('qualityStandardsSubtitle')}
                        </p>
                    </div>
                </section>

                {/* Certifications */}
                <section className="py-20 px-6">
                    <div className="max-w-6xl mx-auto">
                        <h2 className="text-3xl font-headline font-bold text-center mb-12">{t('requiredCertifications')}</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {certifications.map((cert, i) => (
                                <div key={i} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
                                    <Icon name={cert.icon as any} size={32} variant="outline" className="text-primary mb-4" />
                                    <h3 className="font-headline font-bold text-foreground mb-2">{cert.name}</h3>
                                    <p className="text-sm text-muted-foreground">{cert.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Quality Process */}
                <section className="py-20 px-6 bg-card">
                    <div className="max-w-5xl mx-auto">
                        <h2 className="text-3xl font-headline font-bold text-center mb-12">{t('ourQualityProcess')}</h2>
                        <div className="grid md:grid-cols-2 gap-8">
                            {qualityProcess.map((step, i) => (
                                <div key={i} className="flex gap-4">
                                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                                        <Icon name={step.icon as any} size={24} variant="outline" className="text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-headline font-semibold text-foreground mb-2">{step.title}</h3>
                                        <p className="text-sm text-muted-foreground">{step.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Quality Commitment */}
                <section className="py-20 px-6">
                    <div className="max-w-4xl mx-auto bg-card border border-border rounded-lg p-12 text-center">
                        <Icon name="AwardIcon" size={48} variant="solid" className="text-primary mx-auto mb-6" />
                        <h2 className="text-2xl font-headline font-bold text-foreground mb-4">
                            {t('committedToExcellence')}
                        </h2>
                        <p className="text-muted-foreground">
                            {t('committedToExcellenceDesc')}
                        </p>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
